import type { AuthenticatedUser } from 'src/auth/types';

import { useMemo, useCallback, useState, useEffect } from 'react';
import { isBrowser } from 'src/hooks/use-is-browser';
import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

const useUserDetailsRetreiver = (): [
  AuthenticatedUser | null,
  boolean,
  () => Promise<AuthenticatedUser | null>,
] => {
  const [fetchedDetails, setFetchedDetails] = useState(false);
  const [ready, setReady] = useState(false);
  const [details, setDetails] = useState<AuthenticatedUser | null>(null);

  const handleGetUserDetails = useCallback<() => Promise<AuthenticatedUser | null>>(async () => {
    if (details || fetchedDetails) {
      return null;
    }
    setFetchedDetails(true);
    try {
      if (!isBrowser()) {
        setDetails(null);
        setReady(true);
        return null;
      }
      const response = await fetch('/.auth/me');
      const payload = await response.json();
      const { clientPrincipal } = payload;

      setDetails(clientPrincipal);
      return clientPrincipal;
    } catch (error) {
      // console.error(`Error retrieving user details: ${error}`);
      setDetails(null);
    } finally {
      setReady(true);
    }
    return null;
  }, [details, fetchedDetails, setDetails, setFetchedDetails, setReady]);

  return [details, ready, handleGetUserDetails];
};

function MsalProvider({ children }: Props) {
  const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
  const [details, ready, retriveUserDetails] = useUserDetailsRetreiver();

  const handleLogin = useCallback(async () => {
    // Member content is authorized per request by the API against the pack roster, not by the
    // 'member' role - the Static Web Apps free plan caps invitations at 25 and the roster is much
    // larger. Signing in successfully is enough to land on the site; each page decides for itself.
    await retriveUserDetails();
    window.location.href = '/';
  }, [retriveUserDetails]);

  const handleLogout = useCallback(() => {
    window.location.href = '/logout';
  }, []);

  const handleRoleValidation = useCallback(
    (role: string) => {
      if (details === null || !details.userRoles?.length) {
        return false;
      }
      return details.userRoles!.filter((s) => s === role).length > 0;
    },
    [details]
  );

  useEffect(() => {
    retriveUserDetails();
  }, [retriveUserDetails]);

  useEffect(() => {
    if (ready) {
      setAuthenticated(!!details);
    }
  }, [ready, details, setAuthenticated]);

  // ----------------------------------------------------------------------

  const memoizedValue = useMemo(
    () => ({
      user: {
        ...details,
        photoURL: null,
      },
      method: 'msal',
      // Consumers need to tell "still resolving /.auth/me" apart from "signed out",
      // otherwise a signed-in visitor is shown a sign-in prompt on first paint.
      loading: !ready,
      authenticated: isAuthenticated,
      unauthenticated: !isAuthenticated,
      //
      hasRole: handleRoleValidation,
      login: handleLogin,
      logout: handleLogout,
    }),
    [handleRoleValidation, handleLogin, handleLogout, isAuthenticated, details, ready]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

// ----------------------------------------------------------------------

export function AuthProvider({ children }: Props) {
  return <MsalProvider>{children}</MsalProvider>;
}
